import { Injectable, OnDestroy, signal, Signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SheetActivationService implements OnDestroy {
  // --- private writable signal ---
  private _activeSheet = signal<Excel.Worksheet | null>(null);
  // --- publicly expose only the read‐only interface ---
  readonly activeSheet: Signal<Excel.Worksheet | null> = this._activeSheet;

  // to hold the event‐handler token so we can remove it later
  private onActivatedHandler?: OfficeExtension.EventHandlerResult<Excel.WorksheetActivatedEventArgs>;

  constructor() {
    Office.onReady().then(() => this.initialize());
  }

  public getActiveSheet(): Signal<Excel.Worksheet | null> {
    return this.activeSheet;
  }

  private async initialize() {
    try {
      // 1) Seed the initial active‐worksheet
      await Excel.run(async (ctx) => {
        const sheet = ctx.workbook.worksheets.getActiveWorksheet();
        sheet.load('name');
        await ctx.sync();
        this._activeSheet.set(sheet);
      });

      // 2) Hook the onActivated event and capture its handler token
      await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;

        // the `add` call returns EventHandlerResult<WorksheetActivatedEventArgs>
        this.onActivatedHandler = sheets.onActivated.add(async (args) => {
          try {
            await Excel.run(async (subCtx) => {
              const s = subCtx.workbook.worksheets.getItem(args.worksheetId);
              s.load('name');
              await subCtx.sync();
              this._activeSheet.set(s);
            });
          } catch (e) {
            console.error('Error in onActivated handler', e);
          }
        });

        await ctx.sync(); // commit registration of the handler
      });
    } catch (err) {
      console.error('SheetActivationService initialization failed:', err);
    }
  }

  ngOnDestroy(): void {
    if (this.onActivatedHandler) {
      try {
        this.onActivatedHandler.remove();
      } catch {
        /* might already be removed */
      }
    }
  }
}